import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { DESTINATIONS, PACKAGES } from "@/lib/data";

// Fast global search across all guides, packages, vehicle pages, destinations & journeys.
// The heavy 1,700+ guide index lazy-loads only when the modal is first opened,
// keeping it out of the main JS bundle.
const STATIC_INDEX = [
  ...DESTINATIONS.map((d) => ({ title: d.name, category: "Destination", to: `/destinations/${d.slug}`, key: `d-${d.slug}` })),
  ...PACKAGES.map((p) => ({ title: p.title, category: "Journey", to: `/packages/${p.slug}`, key: `p-${p.slug}` })),
];

export const SearchModal = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [journalIndex, setJournalIndex] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 60);
      if (!journalIndex) {
        import("@/lib/journalData").then((m) => {
          setJournalIndex(
            m.JOURNAL.filter((p) => p.slug).map((p) => ({ title: p.title, category: p.category, to: `/journal/${p.slug}`, key: `j-${p.slug}` }))
          );
        });
      }
    }
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, journalIndex]);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    const terms = t.split(/\s+/).filter(Boolean);
    const index = journalIndex ? [...journalIndex, ...STATIC_INDEX] : STATIC_INDEX;
    const out = [];
    for (const item of index) {
      const hay = `${item.title} ${item.category}`.toLowerCase();
      if (terms.every((w) => hay.includes(w))) out.push(item);
      if (out.length >= 20) break;
    }
    return out;
  }, [q, journalIndex]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-24 md:pt-32" data-testid="search-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[var(--tx-bg-2)] border border-[var(--tx-border)] shadow-2xl">
        <div className="flex items-center gap-3 border-b border-[var(--tx-border)] px-5">
          <Search className="h-5 w-5 text-[var(--tx-gold)] shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
            placeholder="Search 1,700+ guides, tour packages, vehicles & destinations…"
            className="w-full bg-transparent py-5 text-base text-[var(--tx-text)] placeholder:text-[var(--tx-text-2)] focus:outline-none"
          />
          <button onClick={onClose} data-testid="search-close" aria-label="Close search" className="text-[var(--tx-text-2)] hover:text-[var(--tx-gold)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto" data-testid="search-results">
          {q.trim().length >= 2 && results.length === 0 && (
            <p className="px-5 py-8 text-sm text-[var(--tx-text-2)]" data-testid="search-empty">No matches. Try another keyword.</p>
          )}
          {results.map((r) => (
            <Link
              key={r.key}
              to={r.to}
              onClick={onClose}
              data-testid={`search-result-${r.key}`}
              className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-[var(--tx-border)]/60 hover:bg-[var(--tx-gold)]/5 transition-colors duration-200"
            >
              <span className="text-sm text-[var(--tx-text)] line-clamp-1">{r.title}</span>
              <span className="text-[0.62rem] uppercase tracking-[0.16em] text-[var(--tx-gold)] shrink-0">{r.category}</span>
            </Link>
          ))}
          {q.trim().length < 2 && (
            <p className="px-5 py-8 text-sm text-[var(--tx-text-2)]">{'Type at least 2 letters to search — e.g. "Gangtok", "momos", "car rental", "honeymoon".'}</p>
          )}
        </div>
      </div>
    </div>
  );
};
